import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { base_api_url } from "../../../environment.jsx";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Link } from "react-router-dom";

const AddComplementGroupModal = ({ show, onClose, article, onSuccess }) => {
    const MySwal = withReactContent(Swal);
    const [loading, setLoading] = useState(false);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [groupesComplements, setGroupesComplements] = useState([]);
    const [selectedGroupes, setSelectedGroupes] = useState([]);
    const [existingGroupIds, setExistingGroupIds] = useState([]);
    const [nbreGratuit, setNbreGratuit] = useState({});
    const ideresto = JSON.parse(localStorage.getItem("user"))?.restoId;

    // Charger les groupes disponibles
    useEffect(() => {
        if (show && article) {
            fetchGroupesComplements();
            // Récupérer les groupes déjà liés à l'article
            if (article.complement_groups && Array.isArray(article.complement_groups)) {
                setExistingGroupIds(article.complement_groups.map(g => g.id_complement_group));
            }
        }
    }, [show, article]);

    const fetchGroupesComplements = async () => {
        setLoadingGroups(true);
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const response = await axios.get(
                `${base_api_url}cmplt/complement_groups/${ideresto}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setGroupesComplements(response.data.complement_groups || []);
        } catch (error) {
            console.error("Erreur chargement groupes compléments:", error);
            MySwal.fire({
                icon: "error",
                text: "Impossible de charger les groupes de compléments.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
            });
        } finally {
            setLoadingGroups(false);
        }
    };

    const handleNbreGratuitChange = (groupId, value) => {
        if (value < 0) return;
        
        setNbreGratuit(prev => ({
            ...prev,
            [groupId]: value
        }));
    };

    const handleSelectGroup = (groupId) => {
        // Vérifier si le groupe est déjà lié
        if (existingGroupIds.includes(groupId)) {
            MySwal.fire({
                icon: "info",
                text: "Ce groupe est déjà associé à cet article.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 2000,
            });
            return;
        }

        setSelectedGroupes(prev =>
            prev.includes(groupId)
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId]
        );
        if (!selectedGroupes.includes(groupId)) {
            setNbreGratuit(prev => ({
                ...prev,
                [groupId]: 0
            }));
        }
    };

    const handleSubmit = async () => {
        if (selectedGroupes.length === 0) {
            MySwal.fire({
                icon: "warning",
                text: "Veuillez sélectionner au moins un groupe.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 2000,
            });
            return;
        }

        setLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            let successCount = 0;
            let errorCount = 0;

            // Filtrer les groupes déjà liés
            const newGroupIds = selectedGroupes.filter(id => !existingGroupIds.includes(id));

            if (newGroupIds.length === 0) {
                MySwal.fire({
                    icon: "info",
                    text: "Tous les groupes sélectionnés sont déjà associés.",
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 2000,
                });
                setLoading(false);
                return;
            }

            for (const groupId of newGroupIds) {
                try {
                    await axios.post(
                        `${base_api_url}cmplt/article_complement_group`,
                        {
                            id_article: article.id_article,
                            id_complement_group: groupId,
                            nbre_gratuit: nbreGratuit[groupId] || 0 
                        },
                        {
                            headers: { 
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        }
                    );
                    successCount++;
                } catch (error) {
                    if (error.response?.status === 409) {
                        // Déjà existant, on compte comme succès
                        successCount++;
                    } else {
                        errorCount++;
                    }
                }
            }

            // Message de succès
            if (successCount > 0) {
                MySwal.fire({
                    icon: "success",
                    text: `${successCount} groupe${successCount > 1 ? 's' : ''} associé${successCount > 1 ? 's' : ''} avec succès.`,
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
                
                // Mettre à jour les groupes existants
                setExistingGroupIds([...existingGroupIds, ...newGroupIds]);
                
                // Appeler le callback de succès
                if (onSuccess) {
                    onSuccess();
                }
            }

            if (errorCount > 0) {
                MySwal.fire({
                    icon: "warning",
                    text: `${errorCount} groupe${errorCount > 1 ? 's' : ''} n'ont pas pu être associés.`,
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                });
            }

            // Réinitialiser la sélection
            setSelectedGroupes([]);
            onClose();

        } catch (error) {
            console.error("Erreur:", error);
            MySwal.fire({
                icon: "error",
                text: "Une erreur est survenue lors de l'association.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div
            className="modal show fade"
            tabIndex="-1"
            aria-modal="true"
            role="dialog"
            style={{
                display: "block",
                background: "rgba(0,0,0,0.45)",
                zIndex: 9999,
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                overflow: "hidden",
            }}
        >
            <div
                className="modal-dialog modal-dialog-centered"
                style={{
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "90%",
                    maxWidth: "500px",
                    zIndex: 10000,
                }}
            >
                <div
                    className="modal-content"
                    style={{
                        borderRadius: 16,
                        padding: 24,
                        background: "#fff",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                        position: "relative",
                        maxHeight: "80vh",
                        overflowY: "auto",
                    }}
                >
                    {/* Header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                        <h4 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#222" }}>
                            Associer des compléments à <span style={{ color: "#E7592C" }}>{article?.nom}</span>
                        </h4>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                background: "transparent",
                                border: "none",
                                fontSize: 24,
                                cursor: "pointer",
                                color: "#666",
                            }}
                        >
                            ×
                        </button>
                    </div>

                    {/* Groupes déjà liés */}
                    {existingGroupIds.length > 0 && (
                        <div style={{ 
                            marginBottom: 16, 
                            padding: "10px 14px", 
                            backgroundColor: "#E7592C1A", 
                            borderRadius: 8,
                            border: "1px solid #E7592C"
                        }}>
                            <div style={{ fontSize: 13, color: "#E7592C", fontWeight: 500 }}>
                                Groupes déjà associés : {existingGroupIds.length}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                                {groupesComplements
                                    .filter(g => existingGroupIds.includes(g.id_complement_group))
                                    .map(g => (
                                        <span key={g.id_complement_group} style={{
                                            fontSize: 12,
                                            padding: "2px 10px",
                                            backgroundColor: "#E7592C1A",
                                            borderRadius: 12,
                                            color: "#E7592C"
                                        }}>
                                            {g.nom} ✓
                                        </span>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Liste des groupes */}
                    <div style={{ marginBottom: 20 }}> 
                         <label style={{ fontSize: 14, fontWeight: 500, color: "#333", display: "block", marginBottom: 10 }}>
                            Sélectionnez les groupes à associer :
                        </label>
                        {loadingGroups ? (
                            <div style={{ textAlign: "center", padding: 20, color: "#94a3b8" }}>
                                Chargement des groupes...
                            </div>
                        ) : groupesComplements.length === 0 ? (
                             <div>
                                <div style={{ textAlign: "center", padding: 20, color: "#94a3b8" }}>
                                    Aucun groupe de compléments disponible.
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>                    
                                    <Link to="/complements" className="drawer-link-btn" onClick={() => onClose()}>
                                        Créer un groupe
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M7 17L17 7" />
                                            <path d="M7 7h10v10" />
                                        </svg>
                                    </Link>
                               </div>
                           </div>
                        ) : (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>                     
                                {groupesComplements.map(groupe => {
                                    const isSelected = selectedGroupes.includes(groupe.id_complement_group);
                                    const isAlreadyLinked = existingGroupIds.includes(groupe.id_complement_group);
                                    
                                    return (
                                        <button
                                            key={groupe.id_complement_group}
                                            type="button"
                                            onClick={() => handleSelectGroup(groupe.id_complement_group)}
                                            disabled={isAlreadyLinked}
                                            style={{
                                                padding: "8px 16px",
                                                borderRadius: "20px",
                                                border: isSelected ? "2.5px solid #FF6A3D" : "1.5px solid #e2e8f0",
                                                backgroundColor: isSelected ? "#FFF4F0" : "#fff",
                                                color: isSelected ? "#FF6A3D" : "#334155",
                                                fontWeight: isSelected ? "700" : "500",
                                                fontSize: "14px",
                                                cursor: isAlreadyLinked ? "not-allowed" : "pointer",
                                                transition: "all 0.15s ease",
                                                boxShadow: isSelected ? "0 2px 8px rgba(255,106,61,0.12)" : "none",
                                                opacity: isAlreadyLinked ? 0.5 : 1,
                                            }}
                                        >
                                            {groupe.nom}
                                            {isAlreadyLinked && " ✓"}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {selectedGroupes.length > 0 && (
                            <div style={{ marginTop: 12, fontSize: 13, color: "#E7592C" }}>
                                {selectedGroupes.length} groupe{selectedGroupes.length > 1 ? 's' : ''} sélectionné{selectedGroupes.length > 1 ? 's' : ''}
                            </div>
                        )}
                    </div>
                    {selectedGroupes.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 8 }}>
                                Configurer les compléments gratuits pour cet article :
                            </div>
                            {selectedGroupes.map(groupId => {
                                const groupe = groupesComplements.find(g => g.id_complement_group === groupId);
                                if (!groupe) return null;
                                const currentValue = nbreGratuit[groupId] || 0;
                                return (
                                    <div key={groupId} style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 12,
                                        padding: '8px 12px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: 8,
                                        marginBottom: 8
                                    }}>
                                        <span style={{ fontWeight: 500, minWidth: 100 }}>{groupe.nom}</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                                            <label style={{ fontSize: 13, color: '#555' }}>Gratuits:</label>
                                            
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                background: '#fff',
                                                borderRadius: 6,
                                                border: '1px solid #e2e8f0',
                                                overflow: 'hidden'
                                            }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleNbreGratuitChange(groupId, currentValue - 1)}
                                                    disabled={currentValue <= 0}
                                                    style={{
                                                        width: 30,
                                                        height: 30,
                                                        border: 'none',
                                                        background: 'transparent',
                                                        cursor: currentValue <= 0 ? 'not-allowed' : 'pointer',
                                                        fontSize: 16,
                                                        fontWeight: 'bold',
                                                        color: currentValue <= 0 ? '#ccc' : '#E7592C',
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    −
                                                </button>

                                                <span style={{
                                                    minWidth: 35,
                                                    textAlign: 'center',
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    color: '#333',
                                                    padding: '0 4px'
                                                }}>
                                                    {currentValue}
                                                </span>

                                                <button
                                                    type="button"
                                                    onClick={() => handleNbreGratuitChange(groupId, currentValue + 1)}
                                                    style={{
                                                        width: 30,
                                                        height: 30,
                                                        border: 'none',
                                                        background: 'transparent',
                                                        cursor: 'pointer',
                                                        fontSize: 16,
                                                        fontWeight: 'bold',
                                                        color: '#E7592C',
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                padding: "10px 24px",
                                borderRadius: 8,
                                border: "1px solid #e2e8f0",
                                background: "#fff",
                                color: "#64748b",
                                fontWeight: 500,
                                cursor: "pointer",
                            }}
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading || selectedGroupes.length === 0}
                            style={{
                                padding: "10px 24px",
                                borderRadius: 8,
                                border: "none",
                                background: "#E7592C",
                                color: "#fff",
                                fontWeight: 600,
                                cursor: loading || selectedGroupes.length === 0 ? "not-allowed" : "pointer",
                                opacity: loading || selectedGroupes.length === 0 ? 0.6 : 1,
                            }}
                        >
                            {loading ? "Association..." : "Associer"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

AddComplementGroupModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    article: PropTypes.object,
    onSuccess: PropTypes.func,
};

export default AddComplementGroupModal;